import { FormikProvider, useFormik } from 'formik'
import { useNavigate, useLocation } from 'react-router-dom'
import * as Yup from 'yup'

import {
  Alert,
  Button,
  FormField,
  PasswordCriteria,
  Spacer,
  TextInput,
  usePasswordCriteria,
  Typography
} from '@percent/lemonade'
import { useMutation, useDefaultPasswordCriteria } from '@percent/cause-dashboard/common/hooks'
import { useTranslation } from 'react-i18next'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { passwordRequirementRegExp } from '@percent/cause-dashboard/common/utility/validation'
import styles from './SetPassword.module.scss'
import { CentralCardLayoutWrapper } from '@percent/cause-dashboard/common/components'

export function SetPassword() {
  const { authService } = useServices()
  const { search } = useLocation()
  const navigate = useNavigate()
  const hash = new URLSearchParams(search).get('userHash')
  const [{ success, error, errorMessage, isLoading }, { apiFunc: setPassword }] = useMutation(authService.setPassword)
  const { t } = useTranslation()
  const defaultPasswordCriteria = useDefaultPasswordCriteria()

  // The backend is the single gate (flag `be_require_two_fa_for_password_reset`): it only asks for a
  // code when 2FA is enforced, so the field simply follows the error the API returns.
  const otpPrompt = error?.code === 'auth/requires_two_fa_reset_password'
  const twoFaRequired = otpPrompt || error?.code === 'auth/two_fa_invalid_otp_code'

  // The first OTP prompt is an expected next step, not a failure — don't surface it as an error.
  // Real problems (invalid code, rate limits, expired link, …) still show.
  const displayErrorMessage = otpPrompt ? undefined : errorMessage

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      otpCode: ''
    },
    validationSchema: () =>
      Yup.object().shape({
        newPassword: Yup.string()
          .min(8, t('errorMessage.minimum8Characters'))
          .matches(passwordRequirementRegExp, t('errorMessage.passwordRequirement'))
          .max(64)
          .required('Required'),
        otpCode: twoFaRequired
          ? Yup.string()
              .required('Required')
              .matches(/^\d{6}$/, t('errorMessage.max6Digit'))
          : Yup.string()
      }),
    onSubmit: ({ newPassword, otpCode }: { newPassword: string; otpCode: string }) => {
      if (!hash) {
        navigate('/signin')
      } else {
        setPassword({ password: newPassword, hash, otpCode: otpCode || undefined })
      }
    }
  })

  const { errors, values, touched, handleChange, handleBlur, handleSubmit } = formik
  const shouldShowPasswordCriteria = values.newPassword.length > 0 || touched.newPassword

  const { isValid } = usePasswordCriteria(defaultPasswordCriteria, values.newPassword)
  const otpValid = !twoFaRequired || /^\d{6}$/.test(values.otpCode)

  const hashInvalid = !hash || error?.code === 'auth/link_invalid'

  if (success) {
    return (
      <CentralCardLayoutWrapper heading={t('typography.passwordSet')}>
        <Typography className={styles.passwordSetSubtitle}>{t('typography.passwordSetSubtitle')}</Typography>
        <Button stretch size="large" type="button" data-testid="log-in-button" onClick={() => navigate('/signin')}>
          {t('typography.signInHere')}
        </Button>
      </CentralCardLayoutWrapper>
    )
  }

  if (hashInvalid) {
    return (
      <CentralCardLayoutWrapper heading={t('typography.setPassword')}>
        <Alert variant={'error'} title={t('errorMessage.somethingWentWrong')}>
          {t('typography.newPasswordInvalidHash')}{' '}
          <a href="/forgot-password">{t('typography.newPasswordInvalidHashLink')}</a>
        </Alert>
      </CentralCardLayoutWrapper>
    )
  }

  return (
    <CentralCardLayoutWrapper
      heading={t('typography.setPassword')}
      handleSubmit={handleSubmit}
      error={displayErrorMessage}
    >
      {!hashInvalid && (
        <>
          <FormikProvider value={formik}>
            <FormField
              label={t('typography.newPassword')}
              status={touched.newPassword && errors.newPassword ? 'danger' : 'default'}
              data-testid="password"
              necessity="required"
            >
              <TextInput
                type="password"
                name="newPassword"
                placeholder={t('typography.newPasswordPlaceholder')}
                onChange={e => {
                  handleChange(e)
                }}
                onBlur={handleBlur}
                value={values.newPassword}
                autoComplete={'new-password'}
              />
            </FormField>
            <Spacer size={4} axis="vertical" />
            <PasswordCriteria
              password={values.newPassword}
              visible={shouldShowPasswordCriteria}
              passwordCriteria={defaultPasswordCriteria}
            />
            {twoFaRequired && (
              <>
                <Spacer size={4} axis="vertical" />
                <Typography variant="default-bold">{t('form.twoStepVerification')}</Typography>
                <Spacer size={2} axis="vertical" />
                <FormField
                  label={t('form.enterSixDigit')}
                  instructions={t('form.twoFaResetInstructions')}
                  status={touched.otpCode && errors.otpCode ? 'danger' : 'default'}
                  statusMessage={touched.otpCode ? errors.otpCode : undefined}
                  data-testid="otpCode"
                  necessity="required"
                >
                  <TextInput
                    name="otpCode"
                    inputMode="numeric"
                    placeholder={t('form.placeholderSixDigit')}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.otpCode}
                    autoComplete="one-time-code"
                  />
                </FormField>
              </>
            )}
          </FormikProvider>
          <Spacer size={8} axis="vertical" />
          <Button
            stretch
            size="large"
            type="submit"
            loading={isLoading}
            disabled={!isValid || !otpValid}
            data-testid="auth-active-button"
          >
            {t('typography.newPasswordButton')}
          </Button>
        </>
      )}
    </CentralCardLayoutWrapper>
  )
}
